/**
 * Enhanced Collection Filters JavaScript
 * Handles collapsible filters, drawer, sorting, layout switching, and custom checkboxes
 */

(function() {
  'use strict';

  // ============================================
  // Helper: Check if we're on desktop
  // ============================================
  function isDesktop() {
    return window.innerWidth > 999;
  }

  // ============================================
  // Collapsible Filter Functionality (DESKTOP SIDEBAR ONLY)
  // ============================================
  function initCollapsibleFilters() {
    // IMPORTANT: Ne cibler QUE la sidebar desktop, pas le drawer mobile
    const collapsibles = document.querySelectorAll('.CollectionInner__Sidebar [data-action="toggle-collapsible"]');
    
    collapsibles.forEach(button => {
      button.addEventListener('click', function() {
        const isExpanded = this.getAttribute('aria-expanded') === 'true';
        this.setAttribute('aria-expanded', !isExpanded);
        
        const inner = this.nextElementSibling;
        if (inner && inner.classList.contains('Collapsible__Inner')) {
          if (!isExpanded) {
            inner.style.maxHeight = inner.scrollHeight + 'px';
          } else {
            inner.style.maxHeight = '0';
          }
        }
      });
    });

    // Auto-expand filters on desktop if configured (sidebar only)
    const autoExpandFilters = document.querySelectorAll('.CollectionInner__Sidebar .Collapsible--autoExpand');
    autoExpandFilters.forEach(collapsible => {
      const button = collapsible.querySelector('[data-action="toggle-collapsible"]');
      const inner = collapsible.querySelector('.Collapsible__Inner');
      
      if (button && inner && window.innerWidth >= 1025) {
        button.setAttribute('aria-expanded', 'true');
        inner.style.maxHeight = inner.scrollHeight + 'px';
      }
    });
  }

  // ============================================
  // Drawer Functionality (Mobile Filters)
  // ============================================
  function initDrawer() {
    const openButtons = document.querySelectorAll('[data-action="open-drawer"]');
    const closeButtons = document.querySelectorAll('[data-action="close-drawer"]');
    
    openButtons.forEach(button => {
      button.addEventListener('click', function() {
        const drawerId = this.getAttribute('data-drawer-id');
        const drawer = document.getElementById(drawerId);
        if (drawer) {
          drawer.setAttribute('aria-hidden', 'false');
          document.body.style.overflow = 'hidden';
        }
      });
    });

    closeButtons.forEach(button => {
      button.addEventListener('click', function() {
        const drawerId = this.getAttribute('data-drawer-id');
        const drawer = document.getElementById(drawerId);
        if (drawer) {
          drawer.setAttribute('aria-hidden', 'true');
          document.body.style.overflow = '';
        }
      });
    });

    // Close drawer on overlay click
    const drawers = document.querySelectorAll('.Drawer');
    drawers.forEach(drawer => {
      drawer.addEventListener('click', function(e) {
        if (e.target === drawer) {
          drawer.setAttribute('aria-hidden', 'true');
          document.body.style.overflow = '';
        }
      });
    });

    // Close drawer on ESC key
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape') {
        const openDrawer = document.querySelector('.Drawer[aria-hidden="false"]');
        if (openDrawer) {
          openDrawer.setAttribute('aria-hidden', 'true');
          document.body.style.overflow = '';
        }
      }
    });
  }

  // ============================================
  // Filter Form Submission
  // ============================================
  function initFilterForm() {
    const forms = document.querySelectorAll('.collection-filters-form');
    
    forms.forEach(form => {
      const checkboxes = form.querySelectorAll('input[type="checkbox"]');
      const priceInputs = form.querySelectorAll('.price-range input');
      
      // Handle checkbox changes
      checkboxes.forEach(checkbox => {
        checkbox.addEventListener('change', function() {
          submitFilterForm(form);
        });
      });

      // Handle ALL checkbox clicks (permettre la désélection en cliquant à nouveau)
      const allFilterCheckboxes = form.querySelectorAll('input[type="checkbox"]');
      allFilterCheckboxes.forEach(checkbox => {
        let wasChecked = false;
        
        // Capturer l'état AVANT le clic
        checkbox.addEventListener('mousedown', function() {
          wasChecked = this.checked;
        });
        
        // Après le clic, si c'était déjà coché, on décoche
        checkbox.addEventListener('click', function(e) {
          if (wasChecked) {
            e.preventDefault();
            this.checked = false;
            const event = new Event('change', { bubbles: true });
            this.dispatchEvent(event);
          }
        });
      });

      // Handle price range changes (with debounce)
      let priceTimeout;
      priceInputs.forEach(input => {
        input.addEventListener('input', function() {
          clearTimeout(priceTimeout);
          priceTimeout = setTimeout(() => {
            submitFilterForm(form);
          }, 500);
        });
      });
    });
  }

  function submitFilterForm(form) {
    const formData = new FormData(form);
    const currentParams = new URLSearchParams(window.location.search);
    const params = new URLSearchParams();
    
    // Préserver SEULEMENT les paramètres non-filtre (sort_by, q, etc.)
    for (const [key, value] of currentParams) {
      if (!key.startsWith('filter.') && key !== 'sort_by') {
        params.set(key, value);
      }
    }
    
    // Préserver sort_by s'il existe
    const sortBy = currentParams.get('sort_by') || formData.get('sort_by');
    if (sortBy) {
      params.set('sort_by', sortBy);
    }
    
    // Ajouter SEULEMENT les filtres actuellement cochés dans le formulaire
    for (const [key, value] of formData) {
      if (value && key.startsWith('filter.')) {
        params.append(key, value);
      }
    }

    // Construire l'URL finale
    const queryString = params.toString();
    const newUrl = queryString 
      ? `${window.location.pathname}?${queryString}`
      : window.location.pathname;
    
    window.location.href = newUrl;
  }

  // ============================================
  // Clear Filters
  // ============================================
  function initClearFilters() {
    const clearButtons = document.querySelectorAll('[data-action="clear-filters"]');
    
    clearButtons.forEach(button => {
      button.addEventListener('click', function() {
        const url = this.getAttribute('data-url');
        if (url) {
          window.location.href = url;
        }
      });
    });
  }

  // ============================================
  // Sort By Popover
  // ============================================
  function initSortPopover() {
    const sortButton = document.querySelector('.CollectionToolbar__Item--sort');
    const popover = document.getElementById('collection-sort-popover');
    
    if (!sortButton || !popover) return;

    sortButton.addEventListener('click', function(e) {
      e.stopPropagation();
      const isHidden = popover.getAttribute('aria-hidden') === 'true';
      popover.setAttribute('aria-hidden', !isHidden);
      sortButton.setAttribute('aria-expanded', !isHidden);
      
      // Position popover
      if (!isHidden) {
        const rect = sortButton.getBoundingClientRect();
        popover.style.top = (rect.bottom + window.scrollY) + 'px';
        popover.style.left = rect.left + 'px';
      }
    });

    // Close popover
    const closeButton = popover.querySelector('[data-action="close-popover"]');
    if (closeButton) {
      closeButton.addEventListener('click', function() {
        popover.setAttribute('aria-hidden', 'true');
        sortButton.setAttribute('aria-expanded', 'false');
      });
    }

    // Handle sort selection
    const sortOptions = popover.querySelectorAll('[data-action="select-value"]');
    sortOptions.forEach(option => {
      option.addEventListener('click', function() {
        const value = this.getAttribute('data-value');
        const url = new URL(window.location);
        url.searchParams.set('sort_by', value);
        window.location.href = url.toString();
      });
    });

    // Close popover when clicking outside
    document.addEventListener('click', function(e) {
      if (!popover.contains(e.target) && e.target !== sortButton) {
        popover.setAttribute('aria-hidden', 'true');
        sortButton.setAttribute('aria-expanded', 'false');
      }
    });
  }

  // ============================================
  // Layout Switching
  // ============================================
  function initLayoutSwitch() {
    const layoutButtons = document.querySelectorAll('[data-action="change-layout-mode"]');
    
    layoutButtons.forEach(button => {
      button.addEventListener('click', function() {
        const gridType = this.getAttribute('data-grid-type');
        const count = this.getAttribute('data-count');
        const productList = document.querySelector('.ProductList');
        
        if (productList) {
          if (gridType === 'mobile') {
            productList.setAttribute('data-mobile-count', count);
          } else if (gridType === 'desktop') {
            productList.setAttribute('data-desktop-count', count);
          }
          
          // Update active state
          const siblingButtons = this.parentElement.querySelectorAll('[data-action="change-layout-mode"]');
          siblingButtons.forEach(btn => btn.classList.remove('is-active'));
          this.classList.add('is-active');
          
          // Save preference to cart attributes
          fetch('/cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              attributes: {
                [`collection_${gridType}_items_per_row`]: count
              }
            })
          });
        }
      });
    });
  }

  // ============================================
  // Price Range Slider
  // ============================================
  function initPriceRange() {
    const priceRanges = document.querySelectorAll('.price-range');
    
    priceRanges.forEach(container => {
      const rangeInputs = container.querySelectorAll('.range-group input[type="range"]');
      const numberInputs = container.querySelectorAll('.input-prefix__field');
      const rangeGroup = container.querySelector('.price-range__range-group');
      
      if (rangeInputs.length !== 2 || numberInputs.length !== 2) return;
      
      const minRange = rangeInputs[0];
      const maxRange = rangeInputs[1];
      const minInput = numberInputs[0];
      const maxInput = numberInputs[1];
      
      function updateRangeVisual() {
        const min = parseFloat(minRange.value);
        const max = parseFloat(maxRange.value);
        const rangeMax = parseFloat(minRange.max);
        
        const minPercent = (min / rangeMax) * 100;
        const maxPercent = (max / rangeMax) * 100;
        
        rangeGroup.style.setProperty('--range-min', minPercent + '%');
        rangeGroup.style.setProperty('--range-max', maxPercent + '%');
      }
      
      // Range input listeners
      minRange.addEventListener('input', function() {
        const min = parseFloat(this.value);
        const max = parseFloat(maxRange.value);
        
        if (min > max) {
          this.value = max;
        }
        
        minInput.value = Math.ceil(this.value);
        updateRangeVisual();
      });
      
      maxRange.addEventListener('input', function() {
        const min = parseFloat(minRange.value);
        const max = parseFloat(this.value);
        
        if (max < min) {
          this.value = min;
        }
        
        maxInput.value = Math.ceil(this.value);
        updateRangeVisual();
      });
      
      // Number input listeners
      minInput.addEventListener('input', function() {
        const value = parseFloat(this.value) || 0;
        minRange.value = value;
        updateRangeVisual();
      });
      
      maxInput.addEventListener('input', function() {
        const value = parseFloat(this.value) || parseFloat(minRange.max);
        maxRange.value = value;
        updateRangeVisual();
      });
      
      // Initialize
      updateRangeVisual();
    });
  }

  // ============================================
  // Custom Styled Checkboxes
  // ============================================
  function createCustomCheckbox(realCheckbox, label) {
    // Créer le faux checkbox
    const fakeCheckbox = document.createElement('span');
    fakeCheckbox.className = 'custom-checkbox';
    fakeCheckbox.style.cssText = `
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 24px;
      height: 24px;
      background: #F6F6F6;
      border: 1.5px solid #D1D1D1;
      box-sizing: border-box;
      cursor: pointer;
      transition: all 0.2s ease;
    `;

    // Créer la checkmark SVG (crochet)
    const checkmark = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    checkmark.setAttribute('class', 'custom-checkmark');
    checkmark.setAttribute('width', '14');
    checkmark.setAttribute('height', '14');
    checkmark.setAttribute('viewBox', '0 0 14 14');
    checkmark.setAttribute('fill', 'none');
    checkmark.style.cssText = `
      position: absolute;
      left: 5px;
      top: 5px;
      opacity: 0;
      transform: scale(0.8);
      transition: opacity 0.2s ease, transform 0.2s ease;
    `;
    
    const checkPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    checkPath.setAttribute('d', 'M2 7L5.5 10.5L12 3');
    checkPath.setAttribute('stroke', '#000000');
    checkPath.setAttribute('stroke-width', '2');
    checkPath.setAttribute('stroke-linecap', 'round');
    checkPath.setAttribute('stroke-linejoin', 'round');
    
    checkmark.appendChild(checkPath);
    fakeCheckbox.appendChild(checkmark);

    // Insérer le faux checkbox
    label.style.position = 'relative';
    label.style.paddingLeft = '36px';
    label.insertBefore(fakeCheckbox, label.firstChild);

    // Fonction pour mettre à jour l'état
    function updateCheckboxState() {
      if (realCheckbox.checked) {
        fakeCheckbox.style.borderColor = '#000000';
        checkmark.style.opacity = '1';
      } else {
        fakeCheckbox.style.borderColor = '#D1D1D1';
        checkmark.style.opacity = '0';
      }
    }

    // Event: Clic sur le faux checkbox
    const clickHandler = function(e) {
      if (e.target === label || e.target === fakeCheckbox || e.target === checkmark) {
        e.preventDefault();
        e.stopPropagation();
        
        realCheckbox.checked = !realCheckbox.checked;
        const event = new Event('change', { bubbles: true });
        realCheckbox.dispatchEvent(event);
        
        updateCheckboxState();
      }
    };

    fakeCheckbox.addEventListener('click', clickHandler);
    label.addEventListener('click', clickHandler);
    realCheckbox.addEventListener('change', updateCheckboxState);

    updateCheckboxState();
  }

  // Fonction pour créer un toggle switch (Disponibilité)
  function createToggleSwitch(realCheckbox, label) {
    // Créer le container du toggle
    const toggleContainer = document.createElement('div');
    toggleContainer.className = 'toggle-switch-container';
    toggleContainer.style.cssText = `
      display: flex;
      align-items: center;
      padding: 16px 0;
      cursor: pointer;
    `;

    // Créer le toggle switch
    const toggleSwitch = document.createElement('span');
    toggleSwitch.className = 'toggle-switch';
    toggleSwitch.style.cssText = `
      position: relative;
      width: 40px;
      height: 24px;
      background: #D1D1D1;
      border-radius: 24px;
      margin-right: 12px;
      transition: background 0.3s ease;
      flex-shrink: 0;
    `;

    // Créer le cercle du toggle
    const toggleCircle = document.createElement('span');
    toggleCircle.className = 'toggle-circle';
    toggleCircle.style.cssText = `
      position: absolute;
      top: 2px;
      left: 2px;
      width: 20px;
      height: 20px;
      background: #FFFFFF;
      border-radius: 50%;
      box-shadow: 0 2px 4px rgba(0,0,0,0.2);
      transition: transform 0.3s ease;
    `;

    toggleSwitch.appendChild(toggleCircle);

    // Créer le texte du label
    const labelText = document.createElement('span');
    labelText.textContent = label.textContent.trim();
    labelText.style.cssText = `
      font-family: 'Open Sans', sans-serif;
      font-size: 16px;
      font-weight: 400;
      line-height: 24px;
      color: #161616;
    `;

    // Vider le label et ajouter le toggle
    label.innerHTML = '';
    label.style.cssText = `
      display: flex;
      align-items: center;
      padding: 0;
      cursor: pointer;
    `;
    
    label.appendChild(toggleSwitch);
    label.appendChild(labelText);

    // Fonction pour mettre à jour l'état
    function updateToggleState() {
      if (realCheckbox.checked) {
        toggleSwitch.style.background = '#314057';
        toggleCircle.style.transform = 'translateX(16px)';
      } else {
        toggleSwitch.style.background = '#D1D1D1';
        toggleCircle.style.transform = 'translateX(0)';
      }
    }

    // Event: Clic sur le toggle
    const clickHandler = function(e) {
      e.preventDefault();
      e.stopPropagation();
      
      realCheckbox.checked = !realCheckbox.checked;
      const event = new Event('change', { bubbles: true });
      realCheckbox.dispatchEvent(event);
      
      updateToggleState();
    };

    label.addEventListener('click', clickHandler);
    realCheckbox.addEventListener('change', updateToggleState);

    updateToggleState();
  }

  function shouldHaveToggle(collapsible) {
    const button = collapsible.querySelector('.Collapsible__Button');
    if (!button) return false;

    const title = button.textContent.trim().toLowerCase();
    
    // Appliquer toggle SEULEMENT à Disponibilité
    return (
      title.includes('disponibilit') ||
      title.includes('availability')
    );
  }

  function shouldHaveCheckbox(collapsible) {
    const button = collapsible.querySelector('.Collapsible__Button');
    if (!button) return false;

    const title = button.textContent.trim().toLowerCase();
    
    // Appliquer checkboxes à : Taille, Couleurs (PAS Disponibilité)
    return (
      title.includes('taille') ||
      title.includes('size') ||
      title.includes('couleur') ||
      title.includes('color') ||
      title.includes('shoe')
    );
  }

  function initCustomCheckboxes() {
    // IMPORTANT: Ne cibler QUE la sidebar desktop, pas le drawer mobile
    const collapsibles = document.querySelectorAll('.CollectionInner__Sidebar .Collapsible');

    collapsibles.forEach(collapsible => {
      // Vérifier si c'est un toggle ou un checkbox
      const isToggle = shouldHaveToggle(collapsible);
      const isCheckbox = shouldHaveCheckbox(collapsible);
      
      if (!isToggle && !isCheckbox) return;

      const checkboxes = collapsible.querySelectorAll('.Linklist__Checkbox');

      checkboxes.forEach(checkbox => {
        if (checkbox.dataset.customized) return;
        checkbox.dataset.customized = 'true';

        const label = checkbox.nextElementSibling;
        if (!label || label.tagName !== 'LABEL') return;
        if (label.querySelector('.custom-checkbox') || label.querySelector('.toggle-switch')) return;

        // Créer soit un toggle soit un checkbox
        if (isToggle) {
          createToggleSwitch(checkbox, label);
        } else {
          createCustomCheckbox(checkbox, label);
        }
      });
    });
  }

  // ============================================
  // Intercepter les liens de vendor/collection
  // ============================================
  function initVendorCollectionLinks() {
    const quickLinks = document.querySelectorAll('.Linklist a[href*="/collections/"]');
    
    quickLinks.forEach(link => {
      link.addEventListener('click', function(e) {
        const href = this.getAttribute('href');
        const match = href.match(/\/collections\/([^\/]+)\/(.+)/);
        
        if (match) {
          e.preventDefault();
          const collectionHandle = match[1];
          const vendorOrType = decodeURIComponent(match[2]);
          const searchUrl = `/search?q=vendor:${encodeURIComponent(vendorOrType)}&type=product`;
          window.location.href = searchUrl;
        }
      });
    });
  }

  // ============================================
  // Intercepter les selects de filtres Shopify
  // ============================================
  function initFilterSelects() {
    setTimeout(() => {
      const allSelects = document.querySelectorAll('select');
      console.log('[Filter Selects] Found', allSelects.length, 'select elements');
      
      allSelects.forEach((select, index) => {
        console.log(`[Filter Selects] Select ${index}:`, {
          name: select.name,
          id: select.id,
          className: select.className,
          firstOptionText: select.options[0]?.text
        });
        
        if (select.dataset.filterHandled) return;
        select.dataset.filterHandled = 'true';
        
        select.addEventListener('change', function(e) {
          console.log('[Filter Selects] Select changed:', this.value);
          
          const selectedValue = this.value;
          
          if (!selectedValue || selectedValue === '' || selectedValue === 'default') {
            console.log('[Filter Selects] Empty value, ignoring');
            return;
          }
          
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          
          // Détecter le préfixe de langue dans l'URL actuelle
          const currentPath = window.location.pathname;
          const localeMatch = currentPath.match(/^\/(fr|en|es|de|it)\//); // Ajouter d'autres langues si nécessaire
          const localePrefix = localeMatch ? `/${localeMatch[1]}` : '';
          
          const baseUrl = `${localePrefix}/collections/all`;
          const currentParams = new URLSearchParams(window.location.search);
          const selectName = this.name || this.id || '';
          const selectText = this.options[0]?.text || '';
          
          let filterParam;
          
          if (selectName.toLowerCase().includes('vendor') || 
              selectName.toLowerCase().includes('brand') ||
              selectText.toLowerCase().includes('marque') ||
              selectText.toLowerCase().includes('brand')) {
            filterParam = `filter.p.vendor=${encodeURIComponent(selectedValue)}`;
            console.log('[Filter Selects] Detected VENDOR filter');
          }
          else if (selectName.toLowerCase().includes('type') ||
                   selectName.toLowerCase().includes('collection') ||
                   selectText.toLowerCase().includes('collection')) {
            filterParam = `filter.p.product_type=${encodeURIComponent(selectedValue)}`;
            console.log('[Filter Selects] Detected PRODUCT_TYPE filter');
          }
          else {
            filterParam = `filter.p.product_type=${encodeURIComponent(selectedValue)}`;
            console.log('[Filter Selects] Using default PRODUCT_TYPE filter');
          }
          
          const sortBy = currentParams.get('sort_by');
          const newUrl = sortBy 
            ? `${baseUrl}?${filterParam}&sort_by=${sortBy}`
            : `${baseUrl}?${filterParam}`;
          
          console.log('[Filter Selects] Redirecting to:', newUrl);
          window.location.href = newUrl;
        }, true);
      });
    }, 500);
  }

  // ============================================
  // Initialize Everything
  // ============================================
  function init() {
    initCollapsibleFilters();
    initDrawer();
    initFilterForm();
    initClearFilters();
    initSortPopover();
    initLayoutSwitch();
    initPriceRange();
    initCustomCheckboxes();
    initVendorCollectionLinks();
    initFilterSelects();
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize on Shopify section reload
  document.addEventListener('shopify:section:load', init);

})();